import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { FloppyDisk, Trash, X, Plus } from "@phosphor-icons/react";

const empty = {
  slug: "",
  name: "",
  tagline: "",
  target: "Students / Professionals",
  description: "",
  address: "",
  city: "Hull",
  postcode: "",
  price_pcm: 0,
  price_pppw: "",
  bedrooms: 0,
  bathrooms: 0,
  available: true,
  available_from: "",
  bills_included: true,
  furnished: true,
  amenities: [],
  images: [],
  hero_image: "",
  external_url: "",
  coming_soon: false,
};

const targets = [
  "Students / Professionals",
  "Professionals",
  "Students",
  "Professionals / Families",
  "Families",
];

export const PropertyEditor = ({ open, onOpenChange, property, onSaved, onDeleted }) => {
  const isNew = !property;
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [amenityInput, setAmenityInput] = useState("");
  const [imageInput, setImageInput] = useState("");

  useEffect(() => {
    if (open) {
      if (property) {
        setForm({
          ...empty,
          ...property,
          price_pppw: property.price_pppw ?? "",
          external_url: property.external_url ?? "",
        });
      } else {
        setForm(empty);
      }
      setAmenityInput("");
      setImageInput("");
    }
  }, [open, property]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const addAmenity = () => {
    const v = amenityInput.trim();
    if (!v) return;
    setForm((f) => ({ ...f, amenities: [...(f.amenities || []), v] }));
    setAmenityInput("");
  };
  const removeAmenity = (i) =>
    setForm((f) => ({ ...f, amenities: f.amenities.filter((_, idx) => idx !== i) }));

  const addImage = () => {
    const v = imageInput.trim();
    if (!v) return;
    setForm((f) => ({
      ...f,
      images: [...(f.images || []), v],
      hero_image: f.hero_image || v,
    }));
    setImageInput("");
  };
  const removeImage = (i) =>
    setForm((f) => {
      const next = f.images.filter((_, idx) => idx !== i);
      return { ...f, images: next, hero_image: next.includes(f.hero_image) ? f.hero_image : next[0] || "" };
    });

  const save = async () => {
    if (isNew && !form.slug.trim()) {
      toast.error("Slug is required (e.g. ventnor-street)");
      return;
    }
    if (!form.name.trim()) {
      toast.error("Name is required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        price_pcm: Number(form.price_pcm) || 0,
        bedrooms: Number(form.bedrooms) || 0,
        bathrooms: Number(form.bathrooms) || 0,
        price_pppw: form.price_pppw === "" || form.price_pppw === null ? null : Number(form.price_pppw),
        external_url: form.external_url?.trim() || null,
      };
      let saved;
      if (isNew) {
        const { data } = await api.post("/properties", payload);
        saved = data;
        toast.success("Property created");
      } else {
        // PATCH only changed-ish fields; we send everything except slug & created_at
        const { slug, id, created_at, ...rest } = payload;
        const { data } = await api.patch(`/properties/${property.slug}`, rest);
        saved = data;
        toast.success("Property updated");
      }
      onSaved?.(saved);
      onOpenChange(false);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not save";
      toast.error(typeof msg === "string" ? msg : "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!property) return;
    if (!window.confirm(`Delete "${property.name}" permanently?`)) return;
    setDeleting(true);
    try {
      await api.delete(`/properties/${property.slug}`);
      toast.success("Property deleted");
      onDeleted?.(property.slug);
      onOpenChange(false);
    } catch (e) {
      toast.error("Could not delete");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="property-editor">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">
            {isNew ? "Add new property" : `Edit · ${property?.name}`}
          </DialogTitle>
          <DialogDescription className="text-sm text-[var(--text-tertiary)]">
            {isNew
              ? "Fill in the details below to create a new listing."
              : "Update any field and save to publish changes immediately."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 mt-2">
          {/* Basics */}
          <Section title="Basics">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {isNew && (
                <Field label="Slug (URL)" sub="e.g. ventnor-street">
                  <input className="input-sharp" value={form.slug} onChange={(e) => set("slug", e.target.value)} data-testid="editor-slug" />
                </Field>
              )}
              <Field label="Name">
                <input className="input-sharp" value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="editor-name" />
              </Field>
              <Field label="Target tenant">
                <select className="input-sharp" value={form.target} onChange={(e) => set("target", e.target.value)} data-testid="editor-target">
                  {targets.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </Field>
              <Field label="Tagline" full>
                <input className="input-sharp" value={form.tagline} onChange={(e) => set("tagline", e.target.value)} data-testid="editor-tagline" />
              </Field>
              <Field label="Description" full>
                <textarea className="input-sharp" rows={5} value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="editor-description" />
              </Field>
            </div>
          </Section>

          {/* Location */}
          <Section title="Location">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Address line" full>
                <input className="input-sharp" value={form.address} onChange={(e) => set("address", e.target.value)} data-testid="editor-address" />
              </Field>
              <Field label="City">
                <input className="input-sharp" value={form.city} onChange={(e) => set("city", e.target.value)} data-testid="editor-city" />
              </Field>
              <Field label="Postcode">
                <input className="input-sharp" value={form.postcode} onChange={(e) => set("postcode", e.target.value)} data-testid="editor-postcode" />
              </Field>
            </div>
          </Section>

          {/* Pricing & spec */}
          <Section title="Pricing & spec">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <Field label="£ per month">
                <input type="number" className="input-sharp" value={form.price_pcm} onChange={(e) => set("price_pcm", e.target.value)} data-testid="editor-pcm" />
              </Field>
              <Field label="£ pppw" sub="optional">
                <input type="number" className="input-sharp" value={form.price_pppw} onChange={(e) => set("price_pppw", e.target.value)} data-testid="editor-pppw" />
              </Field>
              <Field label="Bedrooms">
                <input type="number" className="input-sharp" value={form.bedrooms} onChange={(e) => set("bedrooms", e.target.value)} data-testid="editor-bedrooms" />
              </Field>
              <Field label="Bathrooms">
                <input type="number" className="input-sharp" value={form.bathrooms} onChange={(e) => set("bathrooms", e.target.value)} data-testid="editor-bathrooms" />
              </Field>
              <Field label="Available from">
                <input type="date" className="input-sharp" value={form.available_from} onChange={(e) => set("available_from", e.target.value)} data-testid="editor-availfrom" />
              </Field>
              <Field label="External listing URL" sub="optional">
                <input className="input-sharp" value={form.external_url} onChange={(e) => set("external_url", e.target.value)} data-testid="editor-external" />
              </Field>
            </div>
            <div className="mt-4 flex flex-wrap gap-6">
              <Toggle label="Available" value={form.available} onChange={(v) => set("available", v)} testId="editor-available" />
              <Toggle label="Bills inclusive" value={form.bills_included} onChange={(v) => set("bills_included", v)} testId="editor-bills" />
              <Toggle label="Furnished" value={form.furnished} onChange={(v) => set("furnished", v)} testId="editor-furnished" />
              <Toggle label="Coming soon" value={form.coming_soon} onChange={(v) => set("coming_soon", v)} testId="editor-comingsoon" />
            </div>
          </Section>

          {/* Amenities */}
          <Section title="Amenities">
            <div className="flex gap-2">
              <input
                className="input-sharp flex-1"
                placeholder="e.g. Walk to University"
                value={amenityInput}
                onChange={(e) => setAmenityInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addAmenity(); } }}
                data-testid="editor-amenity-input"
              />
              <button type="button" onClick={addAmenity} className="btn-outline !py-2 text-sm" data-testid="editor-amenity-add">
                <Plus size={16} /> Add
              </button>
            </div>
            {form.amenities?.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3">
                {form.amenities.map((a, i) => (
                  <span key={i} className="inline-flex items-center gap-1 bg-[var(--bg-secondary)] border border-[var(--border-subtle)] px-3 py-1.5 text-sm">
                    {a}
                    <button type="button" onClick={() => removeAmenity(i)} className="ml-1 text-[var(--text-tertiary)] hover:text-red-600">
                      <X size={14} />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </Section>

          {/* Photos */}
          <Section title="Photos" sub="Paste image URLs. The first image is the cover by default — click any thumbnail to set it as cover.">
            <div className="flex gap-2">
              <input
                className="input-sharp flex-1"
                placeholder="https://… or paste a hosted image URL"
                value={imageInput}
                onChange={(e) => setImageInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addImage(); } }}
                data-testid="editor-image-input"
              />
              <button type="button" onClick={addImage} className="btn-outline !py-2 text-sm" data-testid="editor-image-add">
                <Plus size={16} /> Add
              </button>
            </div>
            {form.images?.length > 0 && (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 mt-4">
                {form.images.map((src, i) => {
                  const isCover = src === form.hero_image;
                  return (
                    <div key={i} className={`relative border ${isCover ? "border-[var(--brand)]" : "border-[var(--border-subtle)]"}`}>
                      <button
                        type="button"
                        className="absolute top-1 right-1 z-10 bg-white/90 hover:bg-red-50 text-red-600 p-1"
                        onClick={() => removeImage(i)}
                        title="Remove"
                      >
                        <Trash size={14} />
                      </button>
                      <button
                        type="button"
                        onClick={() => set("hero_image", src)}
                        className="block w-full aspect-[4/3] overflow-hidden bg-[var(--bg-secondary)]"
                        title={isCover ? "Cover image" : "Set as cover"}
                      >
                        <img src={src} alt="" className="w-full h-full object-cover" />
                      </button>
                      {isCover && (
                        <span className="absolute bottom-1 left-1 bg-[var(--brand)] text-white text-[10px] font-bold uppercase tracking-[0.15em] px-2 py-0.5">
                          Cover
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </Section>
        </div>

        <DialogFooter className="mt-6 flex flex-col-reverse sm:flex-row sm:justify-between gap-3">
          {!isNew ? (
            <button onClick={handleDelete} className="btn-outline !border-red-300 !text-red-600 hover:!bg-red-50 text-sm" disabled={deleting} data-testid="editor-delete">
              <Trash size={16} /> {deleting ? "Deleting…" : "Delete property"}
            </button>
          ) : <span />}
          <div className="flex gap-3 justify-end">
            <button onClick={() => onOpenChange(false)} className="btn-outline text-sm">Cancel</button>
            <button onClick={save} disabled={saving} className="btn-primary text-sm" data-testid="editor-save">
              <FloppyDisk size={16} weight="fill" /> {saving ? "Saving…" : isNew ? "Create property" : "Save changes"}
            </button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Section = ({ title, sub, children }) => (
  <div className="border-t border-[var(--border-subtle)] pt-5">
    <div className="label-uppercase">{title}</div>
    {sub && <div className="text-xs text-[var(--text-tertiary)] mt-1">{sub}</div>}
    <div className="mt-3">{children}</div>
  </div>
);

const Field = ({ label, sub, full = false, children }) => (
  <div className={full ? "sm:col-span-2" : ""}>
    <div className="label-uppercase mb-1.5">
      {label} {sub && <span className="text-[var(--text-tertiary)] normal-case tracking-normal font-normal text-[10px]">· {sub}</span>}
    </div>
    {children}
  </div>
);

const Toggle = ({ label, value, onChange, testId }) => (
  <label className="inline-flex items-center gap-2 cursor-pointer select-none" data-testid={testId}>
    <span className="relative inline-flex items-center">
      <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} className="sr-only peer" />
      <span className="w-10 h-6 bg-[var(--bg-tertiary)] peer-checked:bg-[var(--brand)] transition-colors block" />
      <span className="absolute left-0.5 top-0.5 w-5 h-5 bg-white peer-checked:translate-x-4 transition-transform" />
    </span>
    <span className="text-sm">{label}</span>
  </label>
);
